import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { tokens } from '../../theme';

// The release version, set by release/build-release.ps1; a dev server shows 'development'
export const version = process.env.REACT_APP_VERSION || 'development';
const ORE = '#c9a24a';

// A mine section: the ground cut open to show rock layers, with one gold reef. Drawn once from fixed numbers, so it is the same on every load.
const LINE = '#9fc9e2';
const LAYERS = ['#033550', '#043b58', '#06415f', '#084766', '#0a4d6d', '#0c5374'];
const surface = 150;
// The lower edge of each layer: a gentle wave that dips to the right, like bedded rock
const boundary = (k) => {
    const y0 = surface + 70 + k * 95;
    const pts = Array.from({ length: 81 }, (_, i) => {
        const x = i * 20;
        const y = y0 + x * 0.06 + 14 * Math.sin(x / 90 + k * 1.3) + 6 * Math.sin(x / 37 - k);
        return [x, y];
    });
    return pts;
};
const toPath = (pts) => pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join('');
const REEF = 3;

const MineSection = () => {
    const edges = useMemo(() => Array.from({ length: LAYERS.length }, (_, k) => boundary(k)), []);
    const top = Array.from({ length: 81 }, (_, i) => [i * 20, surface + 6 * Math.sin(i / 3)]);
    const reef = edges[REEF];
    return (
        <Box component="svg" viewBox="0 0 1600 1000" preserveAspectRatio="xMidYMid slice" aria-hidden="true"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
            {LAYERS.map((colour, k) => {
                const upper = k === 0 ? top : edges[k - 1];
                return <path key={k} d={`${toPath(upper)}L1600,1000L0,1000Z`} fill={colour} />;
            })}
            {edges.map((pts, k) => k !== REEF && (
                <path key={k} d={toPath(pts)} fill="none" stroke={LINE} strokeOpacity="0.18" />
            ))}
            <path d={toPath(top)} fill="none" stroke={LINE} strokeOpacity="0.4" />
            {/* the gold reef: a band along one layer boundary */}
            <path d={`${toPath(reef)}${toPath(reef.map(([x, y]) => [x, y + 12]).reverse()).replace('M', 'L')}Z`} fill={ORE} fillOpacity="0.45" />
            <path d={toPath(reef)} fill="none" stroke={ORE} strokeWidth="2.5" />
        </Box>
    );
};

// The frame shared by sign-in and site choice. The mine section fills the screen; on it, two equal
// columns share one centre line: who this is (app and company name, as one block) and the step's card.
// The name block starts level with the card's top, so the two read as a pair.
const AuthShell = ({ children }) => (
    <Box sx={{
        position: 'relative', overflow: 'hidden', minHeight: '100vh', boxSizing: 'border-box', bgcolor: tokens.navy, color: '#fff',
        // Placed a fixed distance from the top rather than centred, so a card that grows (an error
        // message) only pushes down what is below it inside the card; the names and card top stay put
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', px: { xs: 2, sm: 4, md: 8 },
        pt: { xs: 4, md: 'max(48px, calc(50vh - 200px))' }, pb: { xs: 4, md: 6 },
    }}>
        <MineSection />
        <Box sx={{
            position: 'relative', width: '100%', maxWidth: 1040,
            display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start',
            columnGap: 12, rowGap: 3,
        }}>
            <Box sx={{ pt: { md: 5 }, px: { xs: 1, md: 0 }, textAlign: { xs: 'center', md: 'left' } }}>
                <Typography sx={{ fontWeight: 500, fontSize: { xs: 15, md: 18 }, color: '#b9d3e3' }}>Inventory Management System</Typography>
                <Typography component="h1" sx={{ fontWeight: 600, fontSize: { xs: 30, md: 48 }, lineHeight: 1.05, mt: 1 }}>Corwood Enterprises</Typography>
            </Box>
            <Box sx={{
                width: '100%', maxWidth: 440, boxSizing: 'border-box', justifySelf: { xs: 'center', md: 'end' },
                bgcolor: tokens.surface, color: tokens.ink, borderRadius: 3, p: { xs: 3, sm: 5 },
                boxShadow: '0 24px 60px rgba(0, 20, 32, 0.45)',
            }}>
                {children}
            </Box>
        </Box>
        <Typography variant="caption" sx={{ position: 'absolute', right: 24, bottom: 12, color: '#9fc9e2' }}>
            Version {version}
        </Typography>
    </Box>
);

export default AuthShell;
